import { Link } from "react-router-dom";
import { useAuth } from "@/providers/AuthProvider";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  MessageSquare,
  Receipt,
  BellRing,
  ShieldCheck,
  Star,
  HelpCircle,
  ArrowLeft,
  Mail,
  Smartphone,
} from "lucide-react";

const faqs = [
  {
    question: "How do I send my bank details to a customer?",
    answer:
      "Go to your Dashboard, tap 'New Customer', fill in their name and contact info, enter the amount, and hit 'Send Bank Details'. Hound fires off an SMS or email with everything they need to pay you directly.",
    icon: MessageSquare,
  },
  {
    question: "Does Hound charge transaction fees?",
    answer:
      "Nope. Your customer pays directly into your bank account. With Hound there are zero transaction fees — you keep every penny.",
    icon: ShieldCheck,
  },
  {
    question: "How do payment reminders work?",
    answer:
      "Once you mark an invoice as unpaid, Hound automatically sends polite SMS and email reminders on a smart schedule until the customer pays. You don't have to lift a finger.",
    icon: BellRing,
  },
  {
    question: "Can I send branded receipts?",
    answer:
      "Yes — when you mark a payment as received, your customer gets a professional PDF receipt with your branding automatically. No extra work needed.",
    icon: Receipt,
  },
  {
    question: "How do review links work?",
    answer:
      "After a customer pays, Hound can send them a review request with links to your Google, Facebook or other review pages. Happy customers can leave a review in one tap.",
    icon: Star,
  },
  {
    question: "Is there an iPhone or Android app?",
    answer:
      "Yes. Hound works great on mobile web and native apps are available on the App Store and Google Play. Manage invoices and customers on the go.",
    icon: Smartphone,
  },
  {
    question: "What if I forget my password?",
    answer:
      "On the sign-in screen tap 'Forgot password?'. We'll ask your security question to verify it's you, then send a password reset link to your email.",
    icon: HelpCircle,
  },
  {
    question: "How do I contact support?",
    answer:
      "Email us at support@houndpayments.co.uk or use the in-app chat. We're UK-based and typically respond within a few hours during business hours.",
    icon: Mail,
  },
];

export default function Help() {
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-background">
      {/* Top nav — only show when not logged in */}
      {!user && (
        <header className="sticky top-0 z-40 bg-background/80 backdrop-blur border-b border-border">
          <div className="container flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2 font-bold text-xl">
              Hound
            </Link>
            <div className="flex items-center gap-2">
              <Link to="/">
                <Button size="sm" className="gap-1.5 bg-mint text-mint-foreground hover:bg-mint-dark">
                  <ArrowLeft className="h-4 w-4" />
                  Back
                </Button>
              </Link>
            </div>
          </div>
        </header>
      )}

      <div className="container py-12 md:py-16 max-w-3xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-3">
            Help Centre
          </h1>
          <p className="text-muted-foreground text-lg">
            Everything you need to get paid faster with Hound.
          </p>
        </div>

        <div className="space-y-4">
          {faqs.map((faq) => {
            const Icon = faq.icon;
            return (
              <Card key={faq.question} className="border-border">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base font-semibold flex items-start gap-3">
                    <div className="mt-0.5 shrink-0 w-8 h-8 rounded-lg bg-mint/20 text-mint-dark flex items-center justify-center">
                      <Icon className="h-4 w-4" />
                    </div>
                    {faq.question}
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0">
                  <p className="text-muted-foreground text-sm leading-relaxed pl-11">
                    {faq.answer}
                  </p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <div className="mt-12 text-center">
          <p className="text-sm text-muted-foreground mb-4">
            Still stuck? We're happy to help.
          </p>
          <a href="mailto:support@houndpayments.co.uk">
            <Button variant="outline" className="gap-2">
              <Mail className="h-4 w-4" />
              Contact Support
            </Button>
          </a>
        </div>
      </div>
    </div>
  );
}
